import { motion } from "framer-motion";
import { ArrowDown, ArrowUp } from "lucide-react";
import { useDeck } from "../../lib/deck-context";
import { fmtM } from "../../lib/model";
import { Card, Eyebrow, Note, Reveal, Section, SectionHeader } from "../deck/primitives";

const MARKET = [
  { q: "Q2 2026", v: 6.7, src: "Kidder Mathews · Inland Empire Retail" },
  { q: "Q3 2026", v: 7.0, src: "Kidder Mathews · CoStar data", latest: true },
];

export default function CapRate() {
  const { m } = useDeck();
  const scen = [
    { k: "downside", label: "Downside", cap: 7.5, why: "Weaker leasing, ground-lease risk or softer capital markets" },
    { k: "base", label: "Partner Base", cap: 7.0, why: "Directly aligned with the latest regional average", base: true },
    { k: "upside", label: "Upside", cap: 6.75, why: "High-quality, newly built, well-leased project" },
  ];
  return (
    <Section testid="section-caprate">
      <SectionHeader num="07" eyebrow="How the Cap Rate Was Selected" title="Capitalization Rate" />
      <div className="grid lg:grid-cols-12 gap-6">
        <Reveal className="lg:col-span-7">
          <Card className="p-8 lg:p-10 h-full">
            <p data-testid="caprate-definition" className="font-display text-2xl md:text-[28px] leading-[1.4] text-ink">
              The capitalization rate represents the return a buyer would require when purchasing the stabilized income stream. A higher capitalization rate produces a lower valuation; a lower capitalization rate produces a higher valuation.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-4">
              <div className="rounded-xl border border-line p-5">
                <div className="flex items-center gap-2 text-sm text-ink"><ArrowUp size={16} className="text-steel" /> Higher cap rate</div>
                <div className="mt-2 flex items-center gap-2 text-sm text-steel"><ArrowDown size={16} /> Lower value</div>
              </div>
              <div className="rounded-xl border border-line p-5">
                <div className="flex items-center gap-2 text-sm text-ink"><ArrowDown size={16} className="text-arq" /> Lower cap rate</div>
                <div className="mt-2 flex items-center gap-2 text-sm text-arq"><ArrowUp size={16} /> Higher value</div>
              </div>
            </div>
          </Card>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5">
          <Card className="p-8 lg:p-10 h-full">
            <Eyebrow>Current Regional Context</Eyebrow>
            <div className="mt-8 space-y-8">
              {MARKET.map((d, i) => (
                <div key={d.q} data-testid={`caprate-market-${d.q.replace(" ", "-").toLowerCase()}`}>
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm text-char">Inland Empire retail · {d.q}</span>
                    <span className={`font-display text-3xl tabular-nums ${d.latest ? "text-arq" : "text-ink"}`}>≈ {d.v.toFixed(1)}%</span>
                  </div>
                  <div className="mt-3 h-2 rounded-full bg-paper overflow-hidden">
                    <motion.div className={`h-full rounded-full ${d.latest ? "bg-arq" : "bg-ink/70"}`} initial={{ width: 0 }} animate={{ width: `${(d.v / 8) * 100}%` }} transition={{ duration: 0.9, delay: 0.2 + i * 0.15 }} />
                  </div>
                  <div className="mt-2 text-xs text-steel">{d.src}</div>
                </div>
              ))}
            </div>
            <div className="mt-10 pt-6 border-t border-line">
              <div className="text-xs text-steel">Therefore use</div>
              <div className="mt-1 font-display text-3xl text-arq">7.00% Partner Base Case</div>
            </div>
          </Card>
        </Reveal>
      </div>

      <div className="mt-6 grid md:grid-cols-[1fr_1.35fr_1fr] gap-5 items-stretch">
        {scen.map((s, i) => (
          <Reveal key={s.k} delay={0.15 + i * 0.07}>
            <div
              data-testid={`cap-scenario-${s.k}`}
              className={`h-full rounded-[16px] border p-8 ${s.base ? "bg-arq border-arq text-white shadow-[0_24px_48px_-24px_rgba(45,110,106,0.6)] md:-my-3 md:py-11" : "bg-white border-line text-char"}`}
            >
              <div className={`font-mono text-[11px] tracking-[0.2em] uppercase ${s.base ? "text-white/75" : "text-steel"}`}>{s.label}{s.base && " · Primary Assumption"}</div>
              <div className={`mt-5 font-display tabular-nums leading-none ${s.base ? "text-7xl" : "text-5xl text-ink/70"}`}>{s.cap.toFixed(2)}%</div>
              <div className={`mt-6 text-sm ${s.base ? "text-white/80" : "text-steel"}`}>{s.why}</div>
              <div className={`mt-6 pt-5 border-t text-sm flex justify-between ${s.base ? "border-white/25" : "border-line"}`}>
                <span className={s.base ? "text-white/70" : "text-steel"}>Implied value at current NOI</span>
                <span className="tabular-nums font-medium">{fmtM(m.valueAt(s.cap))}</span>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <Note className="mt-10">Cap-rate references: Kidder Mathews Inland Empire Retail Market Reports, Q2 2026 (6.7%) and Q3 2026 (7.0%), using CoStar market data. 6.75% is shown only as an upside sensitivity — it is not the underwriting assumption.</Note>
    </Section>
  );
}
